import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, RefreshCw, Briefcase, GraduationCap, Wrench } from 'lucide-react';
import axios from 'axios';

const Designations = () => {
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDeptFilter, setSelectedDeptFilter] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDesig, setEditingDesig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    code: '',
    department_id: '',
    staff_type: 'Teaching',
    level: 1,
    description: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  // Auto-generate code from designation title
  const generateCode = (title) => {
    if (!title) return '';
    const stopWords = ['of', 'and', 'the', 'in', 'for', 'to', 'a', 'an', 'head', 'assistant', 'senior', 'junior'];
    const words = title.split(' ').filter(word => !stopWords.includes(word.toLowerCase()));
    
    if (words.length === 1) {
      return words[0].substring(0, 3).toUpperCase();
    } else {
      return words.map(word => word.charAt(0).toUpperCase()).join('');
    }
  };

  // Handle title change and auto-generate code
  const handleTitleChange = (title) => {
    if (editingDesig) {
      setFormData({ ...formData, title });
    } else {
      const newCode = generateCode(title);
      setFormData({ ...formData, title, code: newCode });
    }
  };

  useEffect(() => {
    fetchDesignations();
    fetchDepartments();
  }, []);

  const fetchDesignations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/designations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDesignations(response.data);
    } catch (error) {
      console.error('Error fetching designations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/departments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingDesig) {
        await axios.put(`${API_URL}/api/master-settings/designations/${editingDesig.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Designation updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/master-settings/designations`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Designation created successfully!');
      }
      fetchDesignations();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving designation:', error);
      alert('Failed to save designation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this designation?')) {
      setLoading(true);
      try {
        await axios.delete(`${API_URL}/api/master-settings/designations/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Designation deleted successfully!');
        fetchDesignations();
      } catch (error) {
        console.error('Error deleting designation:', error);
        alert('Failed to delete designation. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (desig) => {
    setEditingDesig(desig);
    setFormData({
      title: desig.title,
      code: desig.code || '',
      department_id: desig.department_id || '',
      staff_type: desig.staff_type || 'Teaching',
      level: desig.level || 1,
      description: desig.description || ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDesig(null);
    setFormData({ title: '', code: '', department_id: '', staff_type: 'Teaching', level: 1, description: '' });
  };

  const filteredDesignations = designations.filter(desig => {
    const matchesSearch = desig.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDept = selectedDeptFilter === '' || desig.department_id === selectedDeptFilter;
    const matchesType = selectedTypeFilter === '' || desig.staff_type === selectedTypeFilter;
    return matchesSearch && matchesDept && matchesType;
  });

  // Group by staff type
  const teachingDesigs = filteredDesignations.filter(d => d.staff_type === 'Teaching' || !d.staff_type);
  const nonTeachingDesigs = filteredDesignations.filter(d => d.staff_type === 'Non-Teaching');

  // Helper function to get department name - must be defined before groupedByDept
  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept ? dept.name : null;
  };

  // Group by department
  const groupedByDept = filteredDesignations.reduce((acc, desig) => {
    const deptName = getDepartmentName(desig.department_id) || 'Unassigned';
    if (!acc[deptName]) acc[deptName] = [];
    acc[deptName].push(desig);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Designations</h1>
          <p className="text-sm text-gray-500 mt-1">Manage job titles with Teaching/Non-Teaching type</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          disabled={loading}
        >
          <Plus className="h-4 w-4" />
          Add Designation
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
          <p className="text-blue-600 text-sm">Total Designations</p>
          <p className="text-2xl font-bold text-blue-700">{designations.length}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-xl border border-green-200">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-green-600" />
            <p className="text-green-600 text-sm">Teaching</p>
          </div>
          <p className="text-2xl font-bold text-green-700">{teachingDesigs.length}</p>
        </div>
        <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-orange-600" />
            <p className="text-orange-600 text-sm">Non-Teaching</p>
          </div>
          <p className="text-2xl font-bold text-orange-700">{nonTeachingDesigs.length}</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
          <p className="text-purple-600 text-sm">Departments</p>
          <p className="text-2xl font-bold text-purple-700">{Object.keys(groupedByDept).length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search designations..."
              className="pl-10 pr-4 py-2 w-full border rounded-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <select
              value={selectedTypeFilter}
              onChange={(e) => setSelectedTypeFilter(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg"
            >
              <option value="">All Types</option>
              <option value="Teaching">🎓 Teaching</option>
              <option value="Non-Teaching">🔧 Non-Teaching</option>
            </select>
          </div>
          <div>
            <select
              value={selectedDeptFilter}
              onChange={(e) => setSelectedDeptFilter(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg"
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>
          <div className="text-right">
            <span className="text-sm text-gray-600">{filteredDesignations.length} designations</span>
          </div>
        </div>
      </div>

      {/* Designations by Department */}
      <div className="space-y-4">
        {Object.keys(groupedByDept).length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center border">
            <Briefcase className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-2 text-gray-500">No designations found</p>
          </div>
        ) : (
          Object.entries(groupedByDept).map(([deptName, deptDesigs]) => (
            <div key={deptName} className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
                <h3 className="font-semibold text-gray-700">{deptName}</h3>
                <span className="text-xs text-gray-500">{deptDesigs.length} designations</span>
              </div>
              <div className="divide-y">
                {deptDesigs.map(desig => (
                  <div key={desig.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 px-3 py-1 rounded text-sm font-medium text-blue-700">
                        {desig.code || '—'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-800">{desig.title}</p>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            desig.staff_type === 'Non-Teaching' 
                              ? 'bg-orange-100 text-orange-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {desig.staff_type === 'Non-Teaching' ? '🔧 Non-Teaching' : '🎓 Teaching'}
                          </span>
                        </div>
                        {desig.description && (
                          <p className="text-xs text-gray-500">{desig.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(desig)} className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(desig.id)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">{editingDesig ? 'Edit Designation' : 'Add Designation'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Staff Type Selection - First */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Staff Type *</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, staff_type: 'Teaching' })}
                    className={`p-3 border-2 rounded-lg text-center transition-all ${
                      formData.staff_type === 'Teaching'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-300 hover:border-green-300'
                    }`}
                  >
                    <GraduationCap className="h-5 w-5 mx-auto mb-1" />
                    <div className="font-semibold text-sm">Teaching</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, staff_type: 'Non-Teaching' })}
                    className={`p-3 border-2 rounded-lg text-center transition-all ${
                      formData.staff_type === 'Non-Teaching'
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-300 hover:border-orange-300'
                    }`}
                  >
                    <Wrench className="h-5 w-5 mx-auto mb-1" />
                    <div className="font-semibold text-sm">Non-Teaching</div>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Designation Title *</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2 border rounded-lg"
                  value={formData.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="e.g., Assistant Professor"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    required
                    className="flex-1 px-4 py-2 border rounded-lg uppercase"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="Auto-generated"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, code: generateCode(formData.title) })}
                    className="px-3 py-2 bg-gray-100 border rounded-lg hover:bg-gray-200"
                    title="Regenerate"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Auto-generated from title</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <select
                  className="w-full px-4 py-2 border rounded-lg"
                  value={formData.department_id}
                  onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
                >
                  <option value="">-- Select Department (Optional) --</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border rounded-lg"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                />
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={handleCloseModal} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400">
                  {loading ? 'Saving...' : (editingDesig ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Designations;